import React from 'react';
import { Info, CheckCircle } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';

const GoalsValidationStrip = ({
  goals = [],
  minGoals = 0,
  maxGoals = 0,
  criticalRequired = 1,
  className = '',
}) => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';

  const savedGoals = goals.filter(g => g && g.status !== 'DRAFT');
  const totalSaved = savedGoals.length;
  const totalWeightage = savedGoals.reduce((sum, g) => {
    const w = g.weight ?? g.weightage ?? 0;
    const n = parseFloat(w);
    return sum + (isNaN(n) ? 0 : n);
  }, 0);
  const criticalCount = savedGoals.filter(g => g.critical === true).length;

  const weightOk = totalWeightage === 100;
  const minOk = minGoals === 0 || totalSaved >= minGoals;
  const maxOk = maxGoals === 0 || totalSaved <= maxGoals;
  const criticalOk = criticalRequired === 0 || criticalCount >= criticalRequired;

  const allOk = weightOk && minOk && maxOk && criticalOk && totalSaved > 0;

  const message = (() => {
    if (totalSaved === 0) return 'Start by adding your first goal for this cycle.';
    if (!criticalOk) return `Selection required: mark ${criticalRequired - criticalCount} goal${(criticalRequired - criticalCount) === 1 ? '' : 's'} as Critical Focus.`;
    if (!minOk) return `Add ${minGoals - totalSaved} more goal${(minGoals - totalSaved) === 1 ? '' : 's'} to meet the minimum requirement.`;
    if (!maxOk) return `You have exceeded the maximum by ${totalSaved - maxGoals} goal${(totalSaved - maxGoals) === 1 ? '' : 's'}.`;
    if (!weightOk) {
      const diff = 100 - totalWeightage;
      return diff > 0
        ? `Distribute ${diff}% more weight — total must equal 100%.`
        : `Reduce weight by ${Math.abs(diff)}% — total must equal 100%.`;
    }
    return 'All requirements met. Ready for submission.';
  })();

  const containerClass = [
    'h-12 flex items-center justify-between px-5 rounded-xl border transition-colors',
    allOk
      ? (isDark ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-emerald-50 border-emerald-200')
      : (isDark ? 'bg-white/[0.03] border-white/10' : 'bg-slate-50 border-slate-200'),
    className,
  ].join(' ');

  const iconWrapClass = allOk
    ? (isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-600')
    : (isDark ? 'bg-blue-500/15 text-blue-400' : 'bg-blue-50 text-blue-600');

  const messageClass = `text-[13px] font-medium ${isDark ? 'text-white/80' : 'text-slate-700'}`;

  const weightageClass = `text-[13px] font-bold flex items-center gap-2 ${
    weightOk
      ? (isDark ? 'text-emerald-400' : 'text-emerald-600')
      : totalWeightage > 100
        ? (isDark ? 'text-rose-400' : 'text-rose-600')
        : (isDark ? 'text-white/50' : 'text-slate-500')
  }`;

  return (
    <div
      className={containerClass}
      style={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' }}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className={`p-1.5 rounded-md ${iconWrapClass}`}>
          {allOk ? <CheckCircle size={16} /> : <Info size={16} />}
        </div>
        <span className={messageClass}>{message}</span>
      </div>

      <div className="flex items-center gap-4">
        {/* <span className={`text-[11px] font-bold uppercase tracking-[0.12em] ${isDark ? 'text-white/50' : 'text-slate-500'}`}>
          Goals: <span className={isDark ? 'text-white' : 'text-slate-800'}>{totalSaved}{maxGoals ? ` / ${maxGoals}` : ''}</span>
        </span> */}
        <div className={`h-4 w-px ${isDark ? 'bg-white/10' : 'bg-slate-200'}`} />
        <span className={weightageClass}>
          <span className={`text-[11px] font-bold uppercase tracking-[0.12em] ${isDark ? 'text-white/50' : 'text-slate-500'}`}>Weightage:</span>
          <span>{totalWeightage}% / 100%</span>
          {weightOk && <CheckCircle size={14} className={isDark ? 'text-emerald-400' : 'text-emerald-600'} />}
        </span>
      </div>
    </div>
  );
};

export default GoalsValidationStrip;
